import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash, Search, Check, X, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const CongDoanDonGiaManagement = () => {
    // State Management
    const [congDoanDonGias, setCongDoanDonGias] = useState([]);
    const [currentCongDoanDonGia, setCurrentCongDoanDonGia] = useState({
        'NGÀY CÀI ĐẶT': '',
        'HIỆU LỰC TỪ': '',
        'HIỆU LỰC ĐẾN': '',
        'ID CÀI ĐẶT': '',
        'MÃ CÔNG ĐOẠN': '',
        'TÊN CÔNG ĐOẠN': '',
        'NHÓM NĂNG SUẤT': '',
        'CHI TIẾT CÔNG ĐOẠN': '',
        'ĐƠN VỊ TÍNH': '',
        'ĐƠN GIÁ NĂNG SUẤT': '',
        'PP TÍNH NĂNG SUẤT': '',
        'GHI CHÚ': ''
    });
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [congDoanToDelete, setCongDoanToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [dateFilterFrom, setDateFilterFrom] = useState('');
    const [dateFilterTo, setDateFilterTo] = useState('');
    const [nhomFilter, setNhomFilter] = useState('');

    const [nhomOptions, setNhomOptions] = useState([]);
    const [chiTietOptions, setChiTietOptions] = useState([]);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Fetch data
    const fetchCongDoanDonGias = async () => {
        try {
            const response = await authUtils.apiRequest('CONGDOAN_DONGIA', 'Find', {});
            setCongDoanDonGias(response);
        } catch (error) {
            console.error('Error fetching cong doan don gia list:', error);
            toast.error('Lỗi khi tải danh sách công đoạn đơn giá');
        }
    };

    useEffect(() => {
        fetchCongDoanDonGias();
    }, []);

    // Lấy các option sẵn có
    useEffect(() => {
        if (congDoanDonGias.length > 0) {
            // Danh sách nhóm năng suất unique
            const uniqueNhoms = [
                ...new Set(
                    congDoanDonGias
                        .map(item => item['NHÓM NĂNG SUẤT'])
                        .filter(Boolean)
                ),
            ];
            setNhomOptions(uniqueNhoms);

            // Danh sách chi tiết unique
            const uniqueChiTiet = [
                ...new Set(
                    congDoanDonGias
                        .map(item => item['CHI TIẾT CÔNG ĐOẠN'])
                        .filter(Boolean)
                ),
            ];
            setChiTietOptions(uniqueChiTiet);
        }
    }, [congDoanDonGias]);

    // Format date cho input type="date" (yyyy-mm-dd)
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';

        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    // Format date cho ID CÀI ĐẶT (dd/mm/yyyy)
    const formatDateForId = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };

    // Generate ID CÀI ĐẶT từ MÃ CÔNG ĐOẠN và HIỆU LỰC TỪ - HIỆU LỰC ĐẾN
    const generateIdCaiDat = (maCongDoan, hieuLucTu, hieuLucDen) => {
        if (!maCongDoan || !hieuLucTu) return '';

        const tuFormatted = formatDateForId(hieuLucTu);
        const denFormatted = hieuLucDen ? formatDateForId(hieuLucDen) : '';

        return `${maCongDoan}(${tuFormatted}-${denFormatted})`;
    };

    // Generate mã công đoạn from nhóm NS and chi tiết công đoạn - CẢI TIẾN ĐỂ LẤY CẢ SỐ
    const generateMaCongDoan = (nhomNS, chiTiet) => {
        if (!nhomNS || !chiTiet) return '';

        // Hàm lấy chữ cái đầu của mỗi từ
        const getFirstLetters = (text) => {
            return text
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Bỏ dấu tiếng Việt
                .toUpperCase()
                .split(/[\s,]+/)
                .filter(word => word.length > 0)
                .map(word => word.charAt(0))
                .join('');
        };

        const nhomCode = getFirstLetters(nhomNS);

        // Lấy cả chữ và số từ chi tiết công đoạn
        const chiTietCode = chiTiet
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Bỏ dấu tiếng Việt
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, ''); // Chỉ giữ lại chữ cái và số, bỏ ký tự đặc biệt

        return `${nhomCode}-${chiTietCode}`;
    };

    // Modal handlers
    const handleOpenModal = (congDoanDonGia = null) => {
        if (congDoanDonGia) {
            setIsEditMode(true);
            setCurrentCongDoanDonGia({
                'NGÀY CÀI ĐẶT': congDoanDonGia['NGÀY CÀI ĐẶT'] || '',
                'HIỆU LỰC TỪ': congDoanDonGia['HIỆU LỰC TỪ'] || '',
                'HIỆU LỰC ĐẾN': congDoanDonGia['HIỆU LỰC ĐẾN'] || '',
                'ID CÀI ĐẶT': congDoanDonGia['ID CÀI ĐẶT'] || '',
                'MÃ CÔNG ĐOẠN': congDoanDonGia['MÃ CÔNG ĐOẠN'] || '',
                'TÊN CÔNG ĐOẠN': congDoanDonGia['TÊN CÔNG ĐOẠN'] || '',
                'NHÓM NĂNG SUẤT': congDoanDonGia['NHÓM NĂNG SUẤT'] || '',
                'CHI TIẾT CÔNG ĐOẠN': congDoanDonGia['CHI TIẾT CÔNG ĐOẠN'] || '',
                'ĐƠN VỊ TÍNH': congDoanDonGia['ĐƠN VỊ TÍNH'] || '',
                'ĐƠN GIÁ NĂNG SUẤT': congDoanDonGia['ĐƠN GIÁ NĂNG SUẤT'] || '',
                'PP TÍNH NĂNG SUẤT': congDoanDonGia['PP TÍNH NĂNG SUẤT'] || '',
                'GHI CHÚ': congDoanDonGia['GHI CHÚ'] || '',
            });
        } else {
            setIsEditMode(false);
            const today = new Date().toISOString().split('T')[0];
            setCurrentCongDoanDonGia({
                'NGÀY CÀI ĐẶT': today,
                'HIỆU LỰC TỪ': today,
                'HIỆU LỰC ĐẾN': '',
                'ID CÀI ĐẶT': '',
                'MÃ CÔNG ĐOẠN': '',
                'TÊN CÔNG ĐOẠN': '',
                'NHÓM NĂNG SUẤT': '',
                'CHI TIẾT CÔNG ĐOẠN': '',
                'ĐƠN VỊ TÍNH': '',
                'ĐƠN GIÁ NĂNG SUẤT': '',
                'PP TÍNH NĂNG SUẤT': '',
                'GHI CHÚ': '',
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setCurrentCongDoanDonGia({
            'NGÀY CÀI ĐẶT': '',
            'HIỆU LỰC TỪ': '',
            'HIỆU LỰC ĐẾN': '',
            'ID CÀI ĐẶT': '',
            'MÃ CÔNG ĐOẠN': '',
            'TÊN CÔNG ĐOẠN': '',
            'NHÓM NĂNG SUẤT': '',
            'CHI TIẾT CÔNG ĐOẠN': '',
            'ĐƠN VỊ TÍNH': '',
            'ĐƠN GIÁ NĂNG SUẤT': '',
            'PP TÍNH NĂNG SUẤT': '',
            'GHI CHÚ': '',
        });
    };

    // Form handlers
    const handleInputChange = (field, value) => {
        setCurrentCongDoanDonGia(prev => {
            let processedValue = value;

            // Xử lý đặc biệt cho trường đơn giá: cho phép nhập dấu phẩy
            if (field === 'ĐƠN GIÁ NĂNG SUẤT') {
                // Chuyển dấu phẩy thành dấu chấm để lưu giá trị số
                processedValue = value.replace(',', '.');
            }

            const updated = { ...prev, [field]: processedValue };

            // Auto-generate tên công đoạn & mã khi nhóm NS hoặc chi tiết thay đổi
            if (field === 'NHÓM NĂNG SUẤT' || field === 'CHI TIẾT CÔNG ĐOẠN') {
                const nhomNS = field === 'NHÓM NĂNG SUẤT' ? value : updated['NHÓM NĂNG SUẤT'];
                const chiTiet = field === 'CHI TIẾT CÔNG ĐOẠN' ? value : updated['CHI TIẾT CÔNG ĐOẠN'];

                if (nhomNS && chiTiet) {
                    const tenCongDoan = `${nhomNS} - ${chiTiet}`;
                    const maCongDoan = generateMaCongDoan(nhomNS, chiTiet);
                    updated['TÊN CÔNG ĐOẠN'] = tenCongDoan;
                    updated['MÃ CÔNG ĐOẠN'] = maCongDoan;

                    // Auto-generate ID CÀI ĐẶT
                    const idCaiDat = generateIdCaiDat(maCongDoan, updated['HIỆU LỰC TỪ'], updated['HIỆU LỰC ĐẾN']);
                    updated['ID CÀI ĐẶT'] = idCaiDat;
                }
            }

            // Auto-generate ID CÀI ĐẶT khi HIỆU LỰC TỪ hoặc HIỆU LỰC ĐẾN thay đổi
            if (field === 'HIỆU LỰC TỪ' || field === 'HIỆU LỰC ĐẾN') {
                const hieuLucTu = field === 'HIỆU LỰC TỪ' ? value : updated['HIỆU LỰC TỪ'];
                const hieuLucDen = field === 'HIỆU LỰC ĐẾN' ? value : updated['HIỆU LỰC ĐẾN'];

                if (updated['MÃ CÔNG ĐOẠN'] && hieuLucTu) {
                    const idCaiDat = generateIdCaiDat(updated['MÃ CÔNG ĐOẠN'], hieuLucTu, hieuLucDen);
                    updated['ID CÀI ĐẶT'] = idCaiDat;
                }
            }

            return updated;
        });
    };

    const validateCongDoanDonGia = (congDoanDonGia) => {
        const errors = [];
        if (!congDoanDonGia['NGÀY CÀI ĐẶT']) errors.push('Ngày cài đặt không được để trống');
        if (!congDoanDonGia['HIỆU LỰC TỪ']) errors.push('Hiệu lực từ không được để trống');
        if (!congDoanDonGia['NHÓM NĂNG SUẤT']) errors.push('Nhóm năng suất không được để trống');
        if (!congDoanDonGia['CHI TIẾT CÔNG ĐOẠN']) errors.push('Chi tiết công đoạn không được để trống');
        if (!congDoanDonGia['ĐƠN VỊ TÍNH']) errors.push('ĐVT năng suất không được để trống');
        if (!congDoanDonGia['ĐƠN GIÁ NĂNG SUẤT']) errors.push('Đơn giá năng suất không được để trống');

        // Validate dates
        if (congDoanDonGia['HIỆU LỰC ĐẾN'] && congDoanDonGia['HIỆU LỰC TỪ']) {
            if (new Date(congDoanDonGia['HIỆU LỰC ĐẾN']) < new Date(congDoanDonGia['HIỆU LỰC TỪ'])) {
                errors.push('Hiệu lực đến phải sau hiệu lực từ');
            }
        }

        return errors;
    };

    // Save công đoạn đơn giá
    const handleSaveCongDoanDonGia = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const errors = validateCongDoanDonGia(currentCongDoanDonGia);
            if (errors.length > 0) {
                toast.error(errors.join('\n'));
                setIsSubmitting(false);
                return;
            }

            if (isEditMode) {
                await authUtils.apiRequest('CONGDOAN_DONGIA', 'Edit', {
                    Rows: [currentCongDoanDonGia],
                });
                toast.success('Cập nhật công đoạn đơn giá thành công!');
            } else {
                // Check if ID CÀI ĐẶT already exists
                const existingRecords = await authUtils.apiRequest('CONGDOAN_DONGIA', 'Find', {});
                const exists = existingRecords.some(
                    item =>
                        item['ID CÀI ĐẶT'] === currentCongDoanDonGia['ID CÀI ĐẶT']
                );

                if (exists) {
                    toast.error('ID cài đặt này đã tồn tại! Vui lòng kiểm tra lại mã công đoạn và thời gian hiệu lực.');
                    setIsSubmitting(false);
                    return;
                }

                await authUtils.apiRequest('CONGDOAN_DONGIA', 'Add', {
                    Rows: [currentCongDoanDonGia],
                });
                toast.success('Thêm công đoạn đơn giá mới thành công!');
            }

            await fetchCongDoanDonGias();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving cong doan don gia:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu công đoạn đơn giá'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete handlers
    const handleOpenDeleteConfirmation = (congDoanDonGia) => {
        setCongDoanToDelete(congDoanDonGia);
        setShowDeleteConfirmation(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setCongDoanToDelete(null);
    };

    const handleDeleteCongDoanDonGia = async () => {
        if (!congDoanToDelete) return;

        try {
            await authUtils.apiRequest('CONGDOAN_DONGIA', 'Delete', {
                Rows: [{ 'ID CÀI ĐẶT': congDoanToDelete['ID CÀI ĐẶT'] }],
            });
            toast.success('Xóa công đoạn đơn giá thành công!');
            await fetchCongDoanDonGias();
            handleCloseDeleteConfirmation();
        } catch (error) {
            console.error('Error deleting cong doan don gia:', error);
            toast.error('Có lỗi xảy ra khi xóa công đoạn đơn giá');
        }
    };

    // Sorting
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortedCongDoanDonGias = useCallback(() => {
        const sortableRecords = [...congDoanDonGias];
        if (sortConfig.key) {
            sortableRecords.sort((a, b) => {
                const keyA = a[sortConfig.key];
                const keyB = b[sortConfig.key];

                if (sortConfig.key === 'ĐƠN GIÁ NĂNG SUẤT') {
                    const numA = parseFloat(keyA) || 0;
                    const numB = parseFloat(keyB) || 0;
                    if (numA < numB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (numA > numB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                } else if (
                    sortConfig.key.includes('NGÀY') ||
                    sortConfig.key.includes('HIỆU LỰC')
                ) {
                    const dateA = new Date(keyA || '1900-01-01');
                    const dateB = new Date(keyB || '1900-01-01');
                    if (dateA < dateB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (dateA > dateB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                } else {
                    if ((keyA || '') < (keyB || '')) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if ((keyA || '') > (keyB || '')) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                }
            });
        }
        return sortableRecords;
    }, [congDoanDonGias, sortConfig]);

    // Filtering
    const filteredCongDoanDonGias = getSortedCongDoanDonGias().filter(item => {
        const matchesSearch =
            item['TÊN CÔNG ĐOẠN']?.toLowerCase().includes(search.toLowerCase()) ||
            item['MÃ CÔNG ĐOẠN']?.toLowerCase().includes(search.toLowerCase()) ||
            item['ID CÀI ĐẶT']?.toLowerCase().includes(search.toLowerCase()) ||
            item['NHÓM NĂNG SUẤT']?.toLowerCase().includes(search.toLowerCase()) ||
            item['CHI TIẾT CÔNG ĐOẠN']?.toLowerCase().includes(search.toLowerCase()) ||
            item['PP TÍNH NĂNG SUẤT']?.toLowerCase().includes(search.toLowerCase()) ||
            item['GHI CHÚ']?.toLowerCase().includes(search.toLowerCase());

        // Date range filter
        let matchesDateFilter = true;
        if (dateFilterFrom || dateFilterTo) {
            const itemDate = new Date(item['HIỆU LỰC TỪ']);
            if (dateFilterFrom) {
                const fromDate = new Date(dateFilterFrom);
                if (itemDate < fromDate) matchesDateFilter = false;
            }
            if (dateFilterTo) {
                const toDate = new Date(dateFilterTo);
                if (itemDate > toDate) matchesDateFilter = false;
            }
        }

        const matchesNhomFilter =
            !nhomFilter ||
            item['NHÓM NĂNG SUẤT']?.toLowerCase().includes(nhomFilter.toLowerCase());

        return matchesSearch && matchesDateFilter && matchesNhomFilter;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredCongDoanDonGias.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filteredCongDoanDonGias.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [search, dateFilterFrom, dateFilterTo, nhomFilter]);

    // Get sort direction icon
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return null;
        }
        return sortConfig.direction === 'ascending' ? '↑' : '↓';
    };

    // Get unique nhóm năng suất for filter
    const uniqueNhoms = [
        ...new Set(congDoanDonGias.map(item => item['NHÓM NĂNG SUẤT']).filter(Boolean)),
    ];

    // Pagination handlers
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            }
        }

        return pageNumbers;
    };

    return (
        <div className="p-3 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-5 mb-6 border border-gray-100">
                    {/* Header Section */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <h1 className="text-2xl font-bold text-gray-800">Quản lý Công Đoạn & Đơn Giá</h1>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Filter className="w-4 h-4" />
                                {showFilters ? 'Ẩn bộ lọc' : 'Bộ lọc'}
                            </button>
                            <button
                                onClick={() => handleOpenModal()}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm công đoạn
                            </button>
                        </div>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="mb-6 space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo ID, tên, mã, nhóm NS, chi tiết, PP tính năng suất, ghi chú..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        {showFilters && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Từ ngày
                                    </label>
                                    <input
                                        type="date"
                                        value={dateFilterFrom}
                                        onChange={(e) => setDateFilterFrom(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Đến ngày
                                    </label>
                                    <input
                                        type="date"
                                        value={dateFilterTo}
                                        onChange={(e) => setDateFilterTo(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Lọc theo nhóm NS
                                    </label>
                                    <select
                                        value={nhomFilter}
                                        onChange={(e) => setNhomFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Tất cả nhóm</option>
                                        {uniqueNhoms.map(nhom => (
                                            <option key={nhom} value={nhom}>
                                                {nhom}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={() => {
                                            setDateFilterFrom('');
                                            setDateFilterTo('');
                                            setNhomFilter('');
                                            setSearch('');
                                        }}
                                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Xóa bộ lọc
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Statistics cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <h3 className="text-sm text-blue-700 mb-1">Tổng số công đoạn</h3>
                            <p className="text-2xl font-bold text-blue-800">
                                {filteredCongDoanDonGias.length}
                            </p>
                        </div>

                        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                            <h3 className="text-sm text-green-700 mb-1">Đơn giá cao nhất</h3>
                            <p className="text-2xl font-bold text-green-800">
                                {filteredCongDoanDonGias.length > 0
                                    ? Math.max(
                                        ...filteredCongDoanDonGias.map(
                                            c => parseFloat(c['ĐƠN GIÁ NĂNG SUẤT']) || 0
                                        )
                                    ).toLocaleString('vi-VN')
                                    : 0}
                            </p>
                        </div>

                        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                            <h3 className="text-sm text-purple-700 mb-1">Số nhóm NS</h3>
                            <p className="text-2xl font-bold text-purple-800">
                                {uniqueNhoms.length}
                            </p>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                            <h3 className="text-sm text-yellow-700 mb-1">Đơn giá trung bình</h3>
                            <p className="text-2xl font-bold text-yellow-800">
                                {filteredCongDoanDonGias.length > 0
                                    ? (
                                        filteredCongDoanDonGias.reduce(
                                            (sum, c) =>
                                                sum +
                                                (parseFloat(c['ĐƠN GIÁ NĂNG SUẤT']) || 0),
                                            0
                                        ) / filteredCongDoanDonGias.length
                                    ).toLocaleString('vi-VN', {
                                        maximumFractionDigits: 0,
                                    })
                                    : 0}
                            </p>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('ID CÀI ĐẶT')}
                                    >
                                        ID Cài đặt {getSortIcon('ID CÀI ĐẶT')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('HIỆU LỰC TỪ')}
                                    >
                                        Hiệu lực từ {getSortIcon('HIỆU LỰC TỪ')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('HIỆU LỰC ĐẾN')}
                                    >
                                        Hiệu lực đến {getSortIcon('HIỆU LỰC ĐẾN')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('MÃ CÔNG ĐOẠN')}
                                    >
                                        Mã CD {getSortIcon('MÃ CÔNG ĐOẠN')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('TÊN CÔNG ĐOẠN')}
                                    >
                                        Tên công đoạn {getSortIcon('TÊN CÔNG ĐOẠN')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('NHÓM NĂNG SUẤT')}
                                    >
                                        Nhóm NS {getSortIcon('NHÓM NĂNG SUẤT')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('ĐƠN GIÁ NĂNG SUẤT')}
                                    >
                                        Đơn giá NS {getSortIcon('ĐƠN GIÁ NĂNG SUẤT')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('ĐƠN VỊ TÍNH')}
                                    >
                                        ĐVT {getSortIcon('ĐƠN VỊ TÍNH')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('PP TÍNH NĂNG SUẤT')}
                                    >
                                        PP Tính NS {getSortIcon('PP TÍNH NĂNG SUẤT')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('GHI CHÚ')}
                                    >
                                        Ghi chú {getSortIcon('GHI CHÚ')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center"
                                    >
                                        Thao tác
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {currentItems.length > 0 ? (
                                    currentItems.map((item, index) => (
                                        <tr
                                            key={`${item['ID CÀI ĐẶT']}-${index}`}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                                                <div
                                                    className="max-w-[200px] truncate"
                                                    title={item['ID CÀI ĐẶT']}
                                                >
                                                    {item['ID CÀI ĐẶT']}
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {item['HIỆU LỰC TỪ']
                                                    ? new Date(
                                                        item['HIỆU LỰC TỪ']
                                                    ).toLocaleDateString('vi-VN')
                                                    : '—'}
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {item['HIỆU LỰC ĐẾN']
                                                    ? new Date(
                                                        item['HIỆU LỰC ĐẾN']
                                                    ).toLocaleDateString('vi-VN')
                                                    : '—'}
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item['MÃ CÔNG ĐOẠN']}
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-900">
                                                <div
                                                    className="max-w-xs truncate"
                                                    title={item['TÊN CÔNG ĐOẠN']}
                                                >
                                                    {item['TÊN CÔNG ĐOẠN']}
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-700">
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                    {item['NHÓM NĂNG SUẤT']}
                                                </span>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                {parseFloat(
                                                    item['ĐƠN GIÁ NĂNG SUẤT']
                                                ).toLocaleString('vi-VN')}
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {item['ĐƠN VỊ TÍNH']}
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-700">
                                                <div
                                                    className="max-w-xs truncate"
                                                    title={item['PP TÍNH NĂNG SUẤT']}
                                                >
                                                    {item['PP TÍNH NĂNG SUẤT'] || '—'}
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-700">
                                                <div
                                                    className="max-w-xs truncate"
                                                    title={item['GHI CHÚ']}
                                                >
                                                    {item['GHI CHÚ'] || '—'}
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() =>
                                                            handleOpenModal(item)
                                                        }
                                                        className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                        title="Sửa công đoạn"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            handleOpenDeleteConfirmation(
                                                                item
                                                            )
                                                        }
                                                        className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                        title="Xóa công đoạn"
                                                    >
                                                        <Trash className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan="11"
                                            className="px-3 py-6 text-center text-sm text-gray-500"
                                        >
                                            Không tìm thấy công đoạn nào phù hợp với
                                            tiêu chí tìm kiếm
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Section */}
                    {filteredCongDoanDonGias.length > 0 && (
                        <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
                            {/* Items per page selector */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-700">
                                    Hiển thị:
                                </label>
                                <select
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <span className="text-sm text-gray-700">
                                    mục / trang
                                </span>
                            </div>

                            {/* Page info */}
                            <div className="text-sm text-gray-700">
                                Hiển thị {startIndex + 1} - {Math.min(endIndex, filteredCongDoanDonGias.length)} trong tổng số {filteredCongDoanDonGias.length} công đoạn
                            </div>

                            {/* Pagination buttons */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`p-2 rounded-lg border ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                                        } transition-colors`}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>

                                {getPageNumbers().map((pageNum, index) => (
                                    pageNum === '...' ? (
                                        <span
                                            key={`ellipsis-${index}`}
                                            className="px-3 py-1 text-gray-500"
                                        >
                                            ...
                                        </span>
                                    ) : (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    )
                                ))}

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`p-2 rounded-lg border ${currentPage === totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                                        } transition-colors`}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditMode
                                    ? 'Cập nhật công đoạn đơn giá'
                                    : 'Thêm công đoạn đơn giá mới'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ngày cài đặt <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formatDateForInput(
                                            currentCongDoanDonGia['NGÀY CÀI ĐẶT']
                                        )}
                                        onChange={(e) =>
                                            handleInputChange(
                                                'NGÀY CÀI ĐẶT',
                                                e.target.value
                                            )
                                        }
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Hiệu lực từ{' '}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={formatDateForInput(
                                                currentCongDoanDonGia['HIỆU LỰC TỪ']
                                            )}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    'HIỆU LỰC TỪ',
                                                    e.target.value
                                                )
                                            }
                                            className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Hiệu lực đến
                                        </label>
                                        <input
                                            type="date"
                                            value={formatDateForInput(
                                                currentCongDoanDonGia['HIỆU LỰC ĐẾN']
                                            )}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    'HIỆU LỰC ĐẾN',
                                                    e.target.value
                                                )
                                            }
                                            className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                </div>

                                {/* Nhóm năng suất */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nhóm năng suất{' '}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        list="nhom-options"
                                        value={
                                            currentCongDoanDonGia['NHÓM NĂNG SUẤT']
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                'NHÓM NĂNG SUẤT',
                                                e.target.value
                                            )
                                        }
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Chọn hoặc nhập nhóm năng suất"
                                        required
                                    />
                                    <datalist id="nhom-options">
                                        {nhomOptions.map(nhom => (
                                            <option key={nhom} value={nhom} />
                                        ))}
                                    </datalist>
                                </div>

                                {/* Chi tiết công đoạn */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Chi tiết công đoạn{' '}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        list="chitiet-options"
                                        value={
                                            currentCongDoanDonGia['CHI TIẾT CÔNG ĐOẠN']
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                'CHI TIẾT CÔNG ĐOẠN',
                                                e.target.value
                                            )
                                        }
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Chọn hoặc nhập chi tiết công đoạn"
                                        required
                                    />
                                    <datalist id="chitiet-options">
                                        {chiTietOptions.map(chiTiet => (
                                            <option key={chiTiet} value={chiTiet} />
                                        ))}
                                    </datalist>
                                </div>

                                {/* PP TÍNH NĂNG SUẤT */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        PP tính năng suất
                                    </label>
                                    <input
                                        type="text"
                                        value={
                                            currentCongDoanDonGia['PP TÍNH NĂNG SUẤT']
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                'PP TÍNH NĂNG SUẤT',
                                                e.target.value
                                            )
                                        }
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập phương pháp tính năng suất"
                                    />
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4">
                                {/* ID CÀI ĐẶT - Key chính */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ID Cài đặt (Key chính - tự động tạo)
                                    </label>
                                    <input
                                        type="text"
                                        value={currentCongDoanDonGia['ID CÀI ĐẶT']}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full bg-indigo-50 text-indigo-700 font-medium"
                                        readOnly
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Tự động tạo: MÃ CÔNG ĐOẠN(HIỆU LỰC TỪ-HIỆU LỰC ĐẾN)
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Mã công đoạn (tự động tạo)
                                    </label>
                                    <input
                                        type="text"
                                        value={currentCongDoanDonGia['MÃ CÔNG ĐOẠN']}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                        readOnly
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Tự động tạo từ chữ cái đầu và số của tên công đoạn
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tên công đoạn (tự động tạo)
                                    </label>
                                    <input
                                        type="text"
                                        value={currentCongDoanDonGia['TÊN CÔNG ĐOẠN']}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                        readOnly
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Tự động tạo từ: Nhóm năng suất - Chi tiết công đoạn
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ĐVT năng suất{' '}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={currentCongDoanDonGia['ĐƠN VỊ TÍNH']}
                                        onChange={(e) =>
                                            handleInputChange(
                                                'ĐƠN VỊ TÍNH',
                                                e.target.value
                                            )
                                        }
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    >
                                        <option value="">Chọn ĐVT năng suất</option>
                                        <option value="Tấn">Tấn</option>
                                        <option value="Kg">Kg</option>
                                        <option value="M2">M2</option>
                                        <option value="M3">M3</option>
                                        <option value="Cái">Cái</option>
                                        <option value="Lần">Lần</option>
                                        <option value="Giờ">Giờ</option>
                                        <option value="Ngày">Ngày</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Đơn giá năng suất{' '}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={
                                            currentCongDoanDonGia['ĐƠN GIÁ NĂNG SUẤT']
                                        }
                                        onChange={(e) => {
                                            // Chỉ cho phép nhập số, dấu phẩy và dấu chấm
                                            const value = e.target.value;
                                            if (value === '' || /^[0-9]*[,.]?[0-9]*$/.test(value)) {
                                                handleInputChange(
                                                    'ĐƠN GIÁ NĂNG SUẤT',
                                                    value
                                                );
                                            }
                                        }}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập đơn giá (VD: 1000 hoặc 1,5 hoặc 1.5)"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Có thể nhập số thập phân với dấu phẩy (,) hoặc chấm (.)
                                    </p>
                                </div>

                                {/* Ghi chú */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ghi chú
                                    </label>
                                    <textarea
                                        value={currentCongDoanDonGia['GHI CHÚ']}
                                        onChange={(e) =>
                                            handleInputChange(
                                                'GHI CHÚ',
                                                e.target.value
                                            )
                                        }
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        rows={3}
                                        placeholder="Nhập ghi chú (nếu có)"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                disabled={isSubmitting}
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleSaveCongDoanDonGia}
                                disabled={isSubmitting}
                                className={`px-4 py-2 bg-indigo-600 text-white rounded-lg ${isSubmitting
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-indigo-700'
                                    } flex items-center gap-2 transition-colors shadow-sm`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Đang lưu...
                                    </>
                                ) : (
                                    <>
                                        <Check className="h-4 w-4" />
                                        {isEditMode ? 'Cập nhật' : 'Thêm mới'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && congDoanToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                Xác nhận xóa
                            </h2>
                            <button
                                onClick={handleCloseDeleteConfirmation}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                <p className="text-red-700">
                                    Bạn có chắc chắn muốn xóa công đoạn{' '}
                                    <span className="font-bold">
                                        {congDoanToDelete['TÊN CÔNG ĐOẠN']}
                                    </span>
                                    ?
                                </p>
                                <div className="mt-3 text-sm text-red-600">
                                    <p>
                                        <strong>ID Cài đặt:</strong>{' '}
                                        {congDoanToDelete['ID CÀI ĐẶT']}
                                    </p>
                                    <p>
                                        <strong>Mã:</strong>{' '}
                                        {congDoanToDelete['MÃ CÔNG ĐOẠN']}
                                    </p>
                                    <p>
                                        <strong>Đơn giá:</strong>{' '}
                                        {parseFloat(
                                            congDoanToDelete['ĐƠN GIÁ NĂNG SUẤT']
                                        ).toLocaleString('vi-VN')}{' '}
                                        VNĐ
                                    </p>
                                    <p>
                                        <strong>Hiệu lực:</strong>{' '}
                                        {congDoanToDelete['HIỆU LỰC TỪ']
                                            ? new Date(congDoanToDelete['HIỆU LỰC TỪ']).toLocaleDateString('vi-VN')
                                            : '—'}{' '}
                                        đến{' '}
                                        {congDoanToDelete['HIỆU LỰC ĐẾN']
                                            ? new Date(congDoanToDelete['HIỆU LỰC ĐẾN']).toLocaleDateString('vi-VN')
                                            : '—'}
                                    </p>
                                    {congDoanToDelete['PP TÍNH NĂNG SUẤT'] && (
                                        <p>
                                            <strong>PP tính NS:</strong>{' '}
                                            {congDoanToDelete['PP TÍNH NĂNG SUẤT']}
                                        </p>
                                    )}
                                    {congDoanToDelete['GHI CHÚ'] && (
                                        <p>
                                            <strong>Ghi chú:</strong>{' '}
                                            {congDoanToDelete['GHI CHÚ']}
                                        </p>
                                    )}
                                </div>
                                <p className="text-sm text-red-600 mt-2">
                                    Hành động này không thể hoàn tác và có thể ảnh
                                    hưởng đến các báo cáo đã sử dụng công đoạn này.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleCloseDeleteConfirmation}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDeleteCongDoanDonGia}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <Trash className="h-4 w-4" />
                                    Xóa công đoạn
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default CongDoanDonGiaManagement;
